import { ExternalLink, RefreshCw, ShieldCheck } from "lucide-react";

import { Button } from "../../internal/primitives/button";
import { Switch } from "../../internal/primitives/switch";
import { ContentFrame } from "../shell/ContentFrame";
import { WorkspaceHeader } from "../shell/WorkspaceHeader";

export interface PublicStatusMapSettingsViewProps {
  tenantLabel: string;
  enabled: boolean;
  publicUrl?: string;
  canManage: boolean;
  accessDenied?: boolean;
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
  notice?: string | null;
  onEnabledChange: (enabled: boolean) => void;
  onSave: () => void;
  onRefresh: () => void;
}

export function PublicStatusMapSettingsView({
  tenantLabel,
  enabled,
  publicUrl,
  canManage,
  accessDenied = false,
  loading = false,
  saving = false,
  error,
  notice,
  onEnabledChange,
  onSave,
  onRefresh,
}: PublicStatusMapSettingsViewProps) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <WorkspaceHeader
        eyebrow="Status"
        title="Public Map"
        description={`Control the anonymous, privacy-reduced network health map for ${tenantLabel}.`}
      />
      <ContentFrame layout="document" bodyClassName="overflow-y-auto">
        <div className="space-y-5">
          {accessDenied ? (
            <div className="rounded-panel border border-border bg-card p-5">
              <h3 className="text-sm font-bold text-foreground">Status management access required</h3>
              <p className="mt-1 text-xs text-muted-foreground">You cannot view public status-map settings for this tenant.</p>
            </div>
          ) : (
            <>
              <section className="rounded-panel border border-border bg-card p-5 shadow-sm">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-bold text-foreground">Anonymous status map</h3>
                    </div>
                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                      Publication is off by default. When enabled, visitors see coarse link health and approximate geography only.
                    </p>
                  </div>
                  <label className="flex min-h-11 items-center gap-3 rounded-control border border-border px-3 text-xs font-bold text-foreground">
                    <Switch
                      checked={enabled}
                      onCheckedChange={onEnabledChange}
                      disabled={!canManage || loading || saving}
                      aria-label="Enable public status map"
                    />
                    {enabled ? "Published" : "Private"}
                  </label>
                </div>

                <div className="mt-5 rounded-control border border-border bg-muted/20 p-4">
                  <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Public URL</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <code className="min-w-0 flex-1 break-all text-xs text-foreground">{publicUrl || "Public status URL unavailable"}</code>
                    {publicUrl ? (
                      <a className="inline-flex min-h-11 items-center gap-1.5 text-xs font-bold text-primary hover:underline" href={publicUrl} target="_blank" rel="noreferrer">
                        Preview <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </div>
                </div>
              </section>

              <section className="rounded-panel border border-border bg-card p-5 shadow-sm">
                <h3 className="text-sm font-bold text-foreground">Privacy boundary</h3>
                <p className="mt-1 text-xs text-muted-foreground">The public projection intentionally omits:</p>
                <ul className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                  <li>Tenant and canonical inventory identifiers</li>
                  <li>Site, device, interface, and link names</li>
                  <li>Exact coordinates, capacity, rates, and utilization</li>
                  <li>Alerts, diagnostics, topology evidence, and Grafana links</li>
                </ul>
              </section>

              {!canManage ? (
                <p className="rounded-control border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                  Only tenant owners can change publication. You can review the current state.
                </p>
              ) : null}
              {error ? <p className="text-xs text-destructive">{error}</p> : null}
              {notice ? <p className="text-xs text-emerald-700 dark:text-emerald-300">{notice}</p> : null}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onRefresh} disabled={loading || saving}>
                  <RefreshCw className={loading ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
                  Refresh
                </Button>
                <Button onClick={onSave} disabled={!canManage || loading || saving}>
                  {saving ? "Saving…" : "Save publication"}
                </Button>
              </div>
            </>
          )}
        </div>
      </ContentFrame>
    </div>
  );
}
